/**
 * Markable wellness "tools" catalogue — the building blocks of a personal
 * routine. 1:1 with the live app's tool list (ids, Hebrew names, colours,
 * categories) so the premium results match the app exactly. Shared by the
 * routine results page (paid-0) and the per-domain "tools we offer" /
 * "build your routine" sections on the symptoms / THINK / SENSE panels.
 *
 * Names come from shell.tools.<id>.name, the same key the app uses; descriptions
 * + reinforcement insights are i18n keys
 * (results.tools.items.<id>.*). Each tool's photo lives at
 * `assets/tool-icons/<id>.jpg` (exported from the app's TOOL_ICON_IMGS).
 */
export type ToolCat = 'core' | 'body' | 'mind' | 'cognitive' | 'sense';

export interface ToolDef {
  id: string;
  labelKey: string;
  descKey: string;
  insightKey: string;
  /** icon tile tint, behind the photo (fallback when a photo is missing) */
  bg: string;
  /** app category */
  cat: ToolCat;
}

/** URL of a tool's photo (assets are decoded from the app's TOOL_ICON_IMGS). */
export const toolImgUrl = (id: string): string => `assets/tool-icons/${id}.jpg`;

const mk = (id: string, bg: string, cat: ToolCat): ToolDef => ({
  id,
  // The same name the app shows everywhere else (home, catalogue, tool screen).
  labelKey: `shell.tools.${id}.name`,
  descKey: `results.tools.items.${id}.desc`,
  insightKey: `results.tools.items.${id}.insight`,
  bg,
  cat,
});

export const TOOLS: ToolDef[] = [
  // core / sleep
  mk('breathe', '#ECF1EC', 'core'),
  mk('sleepex', '#E8EAF6', 'core'),
  mk('sleep', '#E8EAF6', 'core'),
  mk('tracker', '#FBEAEA', 'core'),
  // mind / mood
  mk('meditate', '#ECF1EC', 'mind'),
  mk('voice', '#F6EAF3', 'mind'),
  mk('mind', '#F6EAF3', 'mind'),
  mk('next', '#F6EAF3', 'mind'),
  // body
  mk('water', '#E4F2FD', 'body'),
  mk('meal', '#FFF0DB', 'body'),
  mk('supps', '#EEECF7', 'body'),
  mk('bone', '#FAECE7', 'body'),
  mk('steps', '#E9F5EA', 'body'),
  mk('kegel', '#FAECE7', 'body'),
  // cognitive
  mk('brain', '#EAEAFB', 'cognitive'),
  mk('myth', '#F3EEFA', 'cognitive'),
  // sense
  mk('eye', '#E8F5F4', 'sense'),
  mk('ear', '#F5E0EE', 'sense'),
  mk('balance', '#EEF2FD', 'sense'),
];

export const TOOLS_BY_ID: Record<string, ToolDef> = TOOLS.reduce(
  (acc, t) => ((acc[t.id] = t), acc),
  {} as Record<string, ToolDef>,
);

/** Recommended tools per results domain (the "tools we offer" highlight). */
export const DOMAIN_TOOLS: Record<string, string[]> = {
  symptoms: ['breathe', 'sleepex', 'tracker', 'water', 'meal'],
  think: ['brain', 'meditate', 'mind', 'next'],
  sense: ['eye', 'ear', 'balance'],
};

/** Default starter routine on the routine page (matches the app's default). */
export const DEFAULT_ROUTINE: string[] = [
  'breathe',
  'water',
  'brain',
  'sleepex',
  'voice',
];

/**
 * "Build your routine" tool matrix — the interactive recommendation grid shown
 * on each domain panel (symptoms / THINK / SENSE). Every domain lays out its
 * pool of tools (`gridIds`) once per sub-domain row, then highlights the
 * topically-relevant ones (`categories[].recommended`); the rest stay dimmed.
 */
export interface ToolCategory {
  key: string;
  labelKey: string;
  /** accent colour for the category dot + label */
  color: string;
  /** tool ids highlighted (lit + checked) by default for this category */
  recommended: string[];
}

export interface DomainMatrix {
  titleKey: string;
  subKey: string;
  /** tool ids in the order they appear across every category row */
  gridIds: string[];
  categories: ToolCategory[];
}

export const DOMAIN_MATRIX: Record<string, DomainMatrix> = {
  symptoms: {
    titleKey: 'results.tools.rec.symptoms.title',
    subKey: 'results.tools.rec.symptoms.sub',
    gridIds: [
      'sleepex', 'sleep', 'tracker', 'meditate', 'breathe', 'voice', 'mind',
      'next', 'water', 'meal', 'supps', 'bone', 'steps', 'kegel',
    ],
    categories: [
      { key: 'sleep', labelKey: 'results.tools.cat.sleep', color: '#1f8079', recommended: ['sleepex', 'sleep', 'tracker', 'meditate', 'breathe'] },
      { key: 'mood', labelKey: 'results.tools.cat.mood', color: '#9b2e8e', recommended: ['voice', 'mind', 'meditate', 'next', 'breathe'] },
      { key: 'body', labelKey: 'results.tools.cat.body', color: '#b07a20', recommended: ['water', 'meal', 'supps', 'bone', 'steps', 'kegel'] },
    ],
  },
  think: {
    titleKey: 'results.tools.rec.think.title',
    subKey: 'results.tools.rec.think.sub',
    gridIds: ['brain', 'myth', 'meditate', 'mind', 'voice', 'next'],
    categories: [
      { key: 'memory', labelKey: 'results.tools.cat.memory', color: '#c48a2c', recommended: ['brain', 'myth'] },
      { key: 'attention', labelKey: 'results.tools.cat.attention', color: '#1f8079', recommended: ['brain', 'meditate'] },
      { key: 'speed', labelKey: 'results.tools.cat.speed', color: '#5B53C8', recommended: ['brain', 'meditate', 'mind'] },
    ],
  },
  sense: {
    titleKey: 'results.tools.rec.sense.title',
    subKey: 'results.tools.rec.sense.sub',
    gridIds: ['eye', 'ear', 'balance'],
    categories: [
      { key: 'vision', labelKey: 'results.tools.cat.vision', color: '#1f8079', recommended: ['eye'] },
      { key: 'hearing', labelKey: 'results.tools.cat.hearing', color: '#8560C0', recommended: ['ear', 'balance'] },
    ],
  },
};
